import React, { useState, useRef, useEffect } from 'react';
import { X, Mail, Lock, Eye, EyeOff, ArrowLeft, Hash, UserPlus, AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { supabase, isSupabaseAvailable } from '../lib/supabase';
import { isSupabaseConfigured } from '../lib/supabase';
import { trackLogin, trackRegistration } from '../utils/analytics';

// Uniform message for every failed sign-up branch, so the response does not
// reveal whether an account already exists for the address that was entered.
const GENERIC_SIGNUP_ERROR =
  "Nous n'avons pas pu créer ce compte. Si vous avez déjà un compte, utilisez la connexion ou la récupération de mot de passe.";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (user: any) => void;
}

export function LoginModal({ isOpen, onClose, onLoginSuccess }: LoginModalProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'login' | 'register' | 'reset'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [eanCode, setEanCode] = useState('');
  const [showEan, setShowEan] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [twoFAState, setTwoFAState] = useState<{
    active: boolean;
    userId: string | null;
    email: string;
    code: string;
    resendCooldown: number;
  }>({ active: false, userId: null, email: '', code: '', resendCooldown: 0 });
  const codeInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isSupabaseConfigured) {
      toast.error('Base de données non configurée. Veuillez cliquer sur "Connect to Supabase" pour configurer la connexion.');
      return;
    }

    setLoading(true);

    try {
      if (mode === 'reset') {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        const response = await fetch(`${supabaseUrl}/functions/v1/request-account-reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({ email }),
        });

        const result = await response.json();

        if (!response.ok) {
          toast.error(result.error || 'Erreur lors de l\'envoi de la demande');
          setLoading(false);
          return;
        }

        toast.success('Votre demande a été envoyée ! Vous recevrez un email lorsque votre compte sera supprimé.');
        setMode('login');

      } else if (mode === 'register') {
        if (password !== confirmPassword) {
          toast.error('Les mots de passe ne correspondent pas');
          setLoading(false);
          return;
        }

        if (password.length < 6) {
          toast.error('Le mot de passe doit contenir au moins 6 caractères');
          setLoading(false);
          return;
        }

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        const normalizedEmail = email.toLowerCase().trim();

        const [managerCheck, simulatorCheck] = await Promise.all([
          supabase.rpc('check_manager_invitation', { p_email: normalizedEmail }),
          supabase.rpc('check_simulator_invitation', { p_email: normalizedEmail }),
        ]);

        const managerInvitation = managerCheck.data?.[0];
        const isManager = managerInvitation?.found === true;
        const simulatorInvitation = simulatorCheck.data?.[0];
        const isSimulator = simulatorInvitation?.found === true;

        let participantInfo: any = null;
        if (!isManager && !isSimulator) {
          if (!eanCode || eanCode.length !== 18) {
            setShowEan(true);
            toast.error('Votre email n\'a pas été trouvé comme gestionnaire ou accès simulateur. Veuillez entrer votre code EAN pour créer un compte participant.');
            setLoading(false);
            return;
          }

          const { data: eanCheck, error: eanCheckError } = await supabase.rpc('check_ean_exists', { p_ean: eanCode, p_email: normalizedEmail });

          if (eanCheckError || !eanCheck || eanCheck.exists !== true) {
            toast.error('Code EAN non trouvé dans notre système. Contactez-nous pour être ajouté à la communauté.');
            setLoading(false);
            return;
          }
          if (eanCheck.email_already_pending === true) {
            toast.error('Vous avez déjà une demande de compte en cours pour ce code EAN. Vous recevrez un email lorsque votre compte sera validé.');
            setLoading(false);
            return;
          }
          if (eanCheck.group_pending === true) {
            toast.error('Une demande de compte est déjà en cours pour un autre point de connexion de votre groupe. Vous ne pouvez pas soumettre une nouvelle demande avant que l\'administrateur ait validé la première.');
            setLoading(false);
            return;
          }

          participantInfo = eanCheck;
        }

        if (isManager) {
          if (managerInvitation.already_registered) {
            toast.error(GENERIC_SIGNUP_ERROR);
            setLoading(false);
            return;
          }

          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email: normalizedEmail,
            password,
            options: {
              emailRedirectTo: `${window.location.origin}/admin/operations`,
              data: {
                is_manager: true,
              },
            },
          });

          if (signUpError) {
            console.error('Sign up error:', signUpError);
            toast.error(GENERIC_SIGNUP_ERROR);
            setLoading(false);
            return;
          }

          let operationName = '';
          if (signUpData.user) {
            const { data: requestResult } = await supabase.rpc('create_manager_account_request', {
              p_email: normalizedEmail,
              p_user_id: signUpData.user.id,
            });
            operationName = requestResult?.[0]?.operation_name || '';
          }

          toast.success(
            operationName
              ? `Compte créé pour ${operationName} ! Votre demande est en attente de validation.`
              : 'Compte créé ! Votre demande est en attente de validation.'
          );
          trackRegistration('email_manager');
          await supabase.auth.signOut();
          onClose();

        } else if (isSimulator) {
          if (simulatorInvitation.already_registered) {
            toast.error(GENERIC_SIGNUP_ERROR);
            setLoading(false);
            return;
          }

          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email: normalizedEmail,
            password,
            options: { data: { is_simulator_guest: true } },
          });

          if (signUpError) {
            console.error('Sign up error:', signUpError);
            toast.error(GENERIC_SIGNUP_ERROR);
            setLoading(false);
            return;
          }

          if (signUpData.user) {
            await supabase.rpc('link_simulator_invitation_user', {
              p_email: normalizedEmail,
              p_user_id: signUpData.user.id,
              p_duration_days: null,
            });
          }

          toast.success('Compte créé ! Votre accès sera disponible dès validation par notre équipe.');
          trackRegistration('email_simulator');
          await supabase.auth.signOut();
          onClose();

        } else {
          const participantId = participantInfo.participant_id as string | undefined;
          const participantName = (participantInfo.name as string | undefined) || '';

          const { data, error } = await supabase.auth.signUp({
            email: normalizedEmail,
            password,
            options: {
              emailRedirectTo: `${window.location.origin}`,
              data: {
                ean_code: eanCode,
                participant_id: participantId,
                name: participantName || undefined,
              },
            },
          });

          if (error) {
            console.error('Sign up error:', error);
            toast.error(GENERIC_SIGNUP_ERROR);
            setLoading(false);
            return;
          }

          const response = await fetch(`${supabaseUrl}/functions/v1/account-validation`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${data.session?.access_token ?? supabaseAnonKey}`,
            },
            body: JSON.stringify({
              action: 'complete_registration',
              participantId,
              name: participantName,
              email: normalizedEmail,
              eanCode,
            }),
          });

          if (!response.ok) {
            toast.error('Compte créé mais erreur lors de l\'association avec le participant. Contactez-nous.');
            setLoading(false);
            return;
          }

          toast.success('Compte créé ! Votre demande est en attente de validation par notre équipe. Vous recevrez un email de confirmation.');
          trackRegistration('email');
          await supabase.auth.signOut();
          onClose();
        }

      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          console.error('Sign in error:', error);
          if (error.message.includes('Email not confirmed')) {
            toast.error('Veuillez confirmer votre email avant de vous connecter.');
          } else {
            toast.error('Email ou mot de passe incorrect.');
          }
        } else if (data?.user) {
          // Check if 2FA is required
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

          const checkResponse = await fetch(`${supabaseUrl}/functions/v1/verify-2fa`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`,
            },
            body: JSON.stringify({ action: 'check', user_id: data.user.id }),
          });
          const checkResult = await checkResponse.json();

          if (checkResult.required) {
            // 2FA needed — send code and switch to 2FA mode
            const sendResponse = await fetch(`${supabaseUrl}/functions/v1/verify-2fa`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${supabaseAnonKey}`,
              },
              body: JSON.stringify({ action: 'send', user_id: data.user.id, email: data.user.email || email }),
            });
            const sendResult = await sendResponse.json();

            if (!sendResponse.ok || !sendResult.success) {
              toast.error('Erreur lors de l\'envoi du code de vérification. Réessayez.');
              await supabase.auth.signOut();
              setLoading(false);
              return;
            }

            toast.success('Un code de vérification a été envoyé à votre adresse email.');
            setTwoFAState({ active: true, userId: data.user.id, email: data.user.email || email, code: '', resendCooldown: 30 });
            setLoading(false);
          } else {
            // No 2FA needed — proceed normally
            trackLogin('email');
            toast.success('Connexion réussie !');
            onLoginSuccess(data.user);
            onClose();
          }
        } else {
          toast.error('Email ou mot de passe incorrect.');
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      toast.error('Une erreur inattendue s\'est produite. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode: 'login' | 'register' | 'reset') => {
    setMode(newMode);
    setPassword('');
    setConfirmPassword('');
    setEanCode('');
    setShowEan(false);
  };

  // Focus code input when 2FA activates
  useEffect(() => {
    if (twoFAState.active && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [twoFAState.active]);

  // Resend cooldown timer
  useEffect(() => {
    if (twoFAState.resendCooldown <= 0) return;
    const timer = setTimeout(() => {
      setTwoFAState(prev => ({ ...prev, resendCooldown: prev.resendCooldown - 1 }));
    }, 1000);
    return () => clearTimeout(timer);
  }, [twoFAState.resendCooldown]);

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFAState.userId || twoFAState.code.length !== 6) return;

    setLoading(true);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const verifyResponse = await fetch(`${supabaseUrl}/functions/v1/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ action: 'verify', user_id: twoFAState.userId, code: twoFAState.code }),
      });
      const verifyResult = await verifyResponse.json();

      if (verifyResult.valid) {
        // Get the current user from the existing session
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          trackLogin('email');
          toast.success('Connexion réussie !');
          onLoginSuccess(user);
          onClose();
        } else {
          toast.error('Session expirée. Veuillez vous reconnecter.');
          await supabase.auth.signOut();
          setTwoFAState({ active: false, userId: null, email: '', code: '', resendCooldown: 0 });
          setMode('login');
        }
      } else {
        toast.error(verifyResult.error || 'Code incorrect.');
      }
    } catch (err) {
      console.error('2FA verify error:', err);
      toast.error('Erreur lors de la vérification. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAResend = async () => {
    if (twoFAState.resendCooldown > 0 || !twoFAState.userId) return;

    setLoading(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const sendResponse = await fetch(`${supabaseUrl}/functions/v1/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ action: 'send', user_id: twoFAState.userId, email: twoFAState.email }),
      });
      const sendResult = await sendResponse.json();

      if (sendResponse.ok && sendResult.success) {
        toast.success('Un nouveau code a été envoyé.');
        setTwoFAState(prev => ({ ...prev, code: '', resendCooldown: 30 }));
      } else {
        toast.error('Erreur lors de l\'envoi du code. Réessayez plus tard.');
      }
    } catch (err) {
      toast.error('Erreur lors de l\'envoi du code.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FACancel = async () => {
    await supabase.auth.signOut();
    setTwoFAState({ active: false, userId: null, email: '', code: '', resendCooldown: 0 });
    setMode('login');
  };

  if (!isOpen) return null;

  const getTitle = () => {
    if (twoFAState.active) return 'Vérification de sécurité';
    switch (mode) {
      case 'reset': return 'Mot de passe oublié';
      case 'register': return 'Créer un compte';
      default: return 'Connexion membre';
    }
  };

  const getSubmitText = () => {
    if (loading) {
      if (twoFAState.active) return 'Vérification...';
      switch (mode) {
        case 'reset': return 'Envoi de la demande...';
        case 'register': return 'Création...';
        default: return 'Connexion...';
      }
    }
    if (twoFAState.active) return 'Valider le code';
    switch (mode) {
      case 'reset': return 'Envoyer la demande';
      case 'register': return 'Créer le compte';
      default: return 'Se connecter';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              {(mode !== 'login' || twoFAState.active) && (
                <button
                  onClick={() => twoFAState.active ? handle2FACancel() : switchMode('login')}
                  className="text-neutral-500 hover:text-neutral-700 transition-colors p-1"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <h2 className="text-2xl font-bold text-neutral-900 font-sans">
                {getTitle()}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-neutral-500 hover:text-neutral-700 transition-colors p-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* === 2FA MODE === */}
          {twoFAState.active ? (
            <>
              <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg">
                <div className="flex items-start space-x-3">
                  <ShieldCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800 mb-1">
                      Vérification de sécurité
                    </p>
                    <p className="text-sm text-amber-700">
                      Pour protéger votre compte, nous avons envoyé un code à 6 chiffres à <strong>{twoFAState.email}</strong>. Saisissez-le ci-dessous pour finaliser votre connexion.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handle2FASubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2 font-sans">
                    Code de vérification
                  </label>
                  <input
                    ref={codeInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={twoFAState.code}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setTwoFAState(prev => ({ ...prev, code: val }));
                    }}
                    className="w-full px-4 py-4 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-neutral-900 text-center text-3xl font-mono tracking-[0.5em]"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || twoFAState.code.length !== 6}
                  className="w-full bg-brand-gold/100 hover:bg-brand-gold text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-sans"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
                      <span>{getSubmitText()}</span>
                    </>
                  ) : (
                    getSubmitText()
                  )}
                </button>
              </form>

              <div className="mt-6 text-center space-y-3">
                <button
                  onClick={handle2FAResend}
                  disabled={twoFAState.resendCooldown > 0 || loading}
                  className="text-sm text-amber-600 hover:text-brand-gold font-medium disabled:opacity-50 disabled:cursor-not-allowed font-sans"
                >
                  {twoFAState.resendCooldown > 0
                    ? `Renvoyer le code (${twoFAState.resendCooldown}s)`
                    : 'Renvoyer le code'}
                </button>

                <div className="pt-4 border-t border-neutral-300">
                  <button
                    onClick={handle2FACancel}
                    className="text-sm text-neutral-600 hover:text-neutral-800 flex items-center justify-center gap-1 w-full font-sans"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Annuler et revenir
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
          {/* === NORMAL MODE (login / register / reset) === */}

          {mode === 'reset' && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-2">
                    Demande de suppression de compte
                  </p>
                  <p className="text-sm text-amber-700 mb-2">
                    Pour des raisons de securite, votre compte actuel sera supprime et vous pourrez en recreer un nouveau avec votre email et votre code EAN.
                  </p>
                  <p className="text-sm text-amber-700">
                    Vous recevrez un email de confirmation lorsque votre compte aura ete supprime par notre equipe.
                  </p>
                </div>
              </div>
            </div>
          )}

          {mode === 'register' && (
            <div className="mb-4 p-3 bg-neutral-50 border border-neutral-200 rounded-lg">
              <p className="text-sm text-neutral-600 font-sans">
                Entrez votre email et choisissez un mot de passe. Votre accès sera déterminé automatiquement en fonction de votre inscription.
              </p>
            </div>
          )}

          {mode === 'login' && (
            <div className="mb-4 p-3 bg-brand-gold/10 border border-brand-gold/30 rounded-lg">
              <p className="text-sm text-brand-gold font-sans">
                <strong>Accès réservé aux membres.</strong> Si vous n'avez pas encore de compte, créez-en un ci-dessous.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2 font-sans">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-neutral-900"
                  placeholder="votre@email.com"
                  required
                />
              </div>
            </div>

            {mode === 'register' && showEan && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2 font-sans">
                  Code EAN (18 chiffres)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Hash className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={eanCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 18) setEanCode(value);
                    }}
                    className="w-full pl-10 pr-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-neutral-900 font-mono"
                    placeholder="541448000000000000"
                    maxLength={18}
                    required
                  />
                </div>
                {eanCode && eanCode.length < 18 && (
                  <p className="text-sm text-neutral-500 mt-1 font-sans">
                    {18 - eanCode.length} chiffres restants
                  </p>
                )}
              </div>
            )}

            {mode !== 'reset' && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2 font-sans">
                  Mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-neutral-900"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2 font-sans">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-neutral-900"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-gold/100 hover:bg-brand-gold text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-sans"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
                  <span>{getSubmitText()}</span>
                </>
              ) : (
                getSubmitText()
              )}
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            {mode === 'login' && (
              <button
                onClick={() => switchMode('reset')}
                className="text-sm text-neutral-600 hover:text-neutral-800 block w-full font-sans"
              >
                Mot de passe oublie ?
              </button>
            )}

            {mode === 'login' && (
              <button
                onClick={() => switchMode('register')}
                className="text-sm text-amber-600 hover:text-brand-gold font-medium flex items-center justify-center gap-2 w-full font-sans"
              >
                <UserPlus className="w-4 h-4" />
                Créer un compte
              </button>
            )}

            <div className="pt-4 border-t border-neutral-300">
              <p className="text-sm text-neutral-600 text-center font-sans">
                {mode === 'register' ? 'Déjà un compte ?' : 'Pas encore membre ?'}{' '}
                <button
                  onClick={mode === 'register' ? () => switchMode('login') : onClose}
                  className="text-amber-600 hover:text-brand-gold font-medium font-sans"
                >
                  {mode === 'register' ? 'Se connecter' : 'Contactez-nous pour rejoindre la communauté'}
                </button>
              </p>
            </div>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
